import axios, { AxiosResponse, CancelToken, AxiosRequestConfig } from 'axios';
import { AuthType } from '../App';

// ===== 型定義 =====

/**
 * ページネーション付きレスポンス型
 */
export interface PaginationData<T> {
  data: T[];
  currentPage: number;
  lastPage: number;
  total: number;
}

/**
 * 標準APIレスポンス型
 */
export interface ResultData<T> {
  data: T;
  success: boolean;
  message?: string;
}

/**
 * エラーレスポンス型
 */
export interface ApiError {
  message: string;
  code?: string;
  details?: Record<string, any>;
}

/**
 * APIメソッドタイプ
 */
export const ApiMethodType = {
  POST: 'post',
  PUT: 'put',
  GET: 'get',
  HEAD: 'head',
  DELETE: 'delete',
  PATCH: 'patch',
} as const;

export type ApiMethodType = (typeof ApiMethodType)[keyof typeof ApiMethodType];

/**
 * API呼び出し設定
 */
export interface ApiConfig<TParams = any> {
  url: string;
  params?: TParams;
  data?: TParams;
  method?: ApiMethodType;
  cancelToken?: CancelToken;
  headers?: Record<string, string>;
  timeout?: number;
}

/**
 * HTTPステータスコード
 */
export const HttpStatusCode = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
} as const;

// ===== ユーティリティ関数 =====

/**
 * キャンセルトークン生成
 */
export const makeCancelTokenSource = () => {
  return axios.CancelToken.source();
};

/**
 * ログアウト処理
 */
function logOut(): void {
  if (window.authType === AuthType.laravel) {
    window.functions.logout();
  }
}

// ===== 型安全なAPIクラス =====

/**
 * 型安全なAPIサービスクラス
 */
export class TypedApiService {
  /**
   * 共通のAxios設定を生成
   */
  private static createAxiosConfig<TParams>(config: ApiConfig<TParams>): AxiosRequestConfig {
    const method = config.method || ApiMethodType.GET;

    console.log('APIリクエスト:', window.baseUrl, config.url, window.accessToken);

    const axiosConfig: AxiosRequestConfig = {
      method: method as any,
      url: window.baseUrl + config.url,
      headers: {
        Authorization: `Bearer ${window.accessToken}`,
        'Content-Type': 'application/json',
        ...config.headers,
      },
      cancelToken: config.cancelToken,
      timeout: config.timeout || 30000,
    };

    // GETリクエストはparams、その他はdataを使用
    if (method === ApiMethodType.GET || method === ApiMethodType.HEAD) {
      axiosConfig.params = config.params;
    } else {
      axiosConfig.data = config.data || config.params;
    }

    return axiosConfig;
  }

  /**
   * エラーハンドリング
   */
  private static handleError(error: any): never {
    if (axios.isCancel(error)) {
      console.warn('Request canceled:', error.message);
      throw new Error('Request was canceled');
    }

    if (error.response) {
      const status = error.response.status;

      if (status === HttpStatusCode.UNAUTHORIZED) {
        logOut();
        throw new Error('Unauthorized access. Please login again.');
      }

      if (status === HttpStatusCode.FORBIDDEN) {
        throw new Error('Access forbidden. Insufficient permissions.');
      }

      if (status === HttpStatusCode.NOT_FOUND) {
        throw new Error('Resource not found.');
      }

      if (status === HttpStatusCode.UNPROCESSABLE_ENTITY) {
        throw new Error('Validation error: ' + (error.response.data?.message || 'Invalid data'));
      }

      throw new Error(error.response.data?.message || `HTTP Error: ${status}`);
    }

    if (error.request) {
      throw new Error('Network error. Please check your connection.');
    }

    throw new Error(error.message || 'An unexpected error occurred.');
  }

  /**
   * 基本API呼び出しメソッド
   */
  public static async request<TResponse, TParams = any>(
    config: ApiConfig<TParams>
  ): Promise<AxiosResponse<TResponse>> {
    try {
      const axiosConfig = this.createAxiosConfig(config);
      const response = (await window.axios(axiosConfig)) as AxiosResponse<TResponse>;
      return response;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * GET リクエスト
   */
  public static async get<TResponse, TParams = any>(
    url: string,
    params?: TParams,
    cancelToken?: CancelToken
  ): Promise<AxiosResponse<TResponse>> {
    return this.request<TResponse, TParams>({
      url,
      params,
      method: ApiMethodType.GET,
      cancelToken,
    });
  }

  /**
   * POST リクエスト
   */
  public static async post<TResponse, TParams = any>(
    url: string,
    params?: TParams,
    cancelToken?: CancelToken
  ): Promise<AxiosResponse<TResponse>> {
    return this.request<TResponse, TParams>({
      url,
      params: params,
      method: ApiMethodType.POST,
      cancelToken,
    });
  }

  /**
   * PUT リクエスト
   */
  public static async put<TResponse, TParams = any>(
    url: string,
    data?: TParams,
    cancelToken?: CancelToken
  ): Promise<AxiosResponse<TResponse>> {
    return this.request<TResponse, TParams>({
      url,
      data,
      method: ApiMethodType.PUT,
      cancelToken,
    });
  }

  /**
   * PATCH リクエスト
   */
  public static async patch<TResponse, TParams = any>(
    url: string,
    data?: TParams,
    cancelToken?: CancelToken
  ): Promise<AxiosResponse<TResponse>> {
    return this.request<TResponse, TParams>({
      url,
      data,
      method: ApiMethodType.PATCH,
      cancelToken,
    });
  }

  /**
   * DELETE リクエスト
   */
  public static async delete<TResponse = void, TParams = any>(
    url: string,
    params?: TParams,
    cancelToken?: CancelToken
  ): Promise<AxiosResponse<TResponse>> {
    return this.request<TResponse, TParams>({
      url,
      params,
      method: ApiMethodType.DELETE,
      cancelToken,
    });
  }

  /**
   * ページネーション対応GETリクエスト
   */
  public static async getPaginated<TItem, TParams = any>(
    url: string,
    params?: TParams,
    cancelToken?: CancelToken
  ): Promise<AxiosResponse<PaginationData<TItem>>> {
    return this.get<PaginationData<TItem>, TParams>(url, params, cancelToken);
  }

  /**
   * 標準レスポンス形式でのGETリクエスト
   */
  public static async getResult<TData, TParams = any>(
    url: string,
    params?: TParams,
    cancelToken?: CancelToken
  ): Promise<AxiosResponse<ResultData<TData>>> {
    return this.get<ResultData<TData>, TParams>(url, params, cancelToken);
  }

  /**
   * 標準レスポンス形式でのPOSTリクエスト
   */
  public static async postResult<TData, TParams = any>(
    url: string,
    data?: TParams,
    cancelToken?: CancelToken
  ): Promise<AxiosResponse<ResultData<TData>>> {
    return this.post<ResultData<TData>, TParams>(url, data, cancelToken);
  }
}

// ===== 従来のAPIとの互換性を保つラッパー関数 =====

/**
 * @deprecated 新しいTypedApiService.getを使用してください
 */
export async function get<T = any>(
  url: string,
  params: any = {},
  cancelToken?: CancelToken
): Promise<AxiosResponse<T>> {
  return TypedApiService.get<T>(url, params, cancelToken);
}

/**
 * @deprecated 新しいTypedApiService.postを使用してください
 */
export async function post<T = any>(
  url: string,
  params: any = {},
  type?: ApiMethodType
): Promise<AxiosResponse<T>> {
  const method = type || ApiMethodType.POST;
  return TypedApiService.request<T>({
    url,
    params,
    method,
  });
}

/**
 * @deprecated 新しいTypedApiService.postを使用してください
 */
export async function postData<T = any>(
  url: string,
  params: any = {},
  type?: ApiMethodType
): Promise<AxiosResponse<T>> {
  const method = type || ApiMethodType.POST;
  return TypedApiService.request<T>({
    url,
    data: params,
    method,
  });
}

/**
 * @deprecated 新しいTypedApiService.requestを使用してください
 */
export async function api<T = any>(args: {
  url: string;
  params?: any;
  type?: ApiMethodType;
  cancelToken?: CancelToken;
}): Promise<AxiosResponse<T>> {
  return TypedApiService.request<T>({
    url: args.url,
    params: args.params,
    method: args.type,
    cancelToken: args.cancelToken,
  });
}

// ===== エクスポート =====
export default TypedApiService;

// 従来の型も互換性のため維持
export interface RequestParam {
  filter: any;
}
